/**
 * Navigation Service
 *
 * This service provides a way to navigate programmatically from anywhere in the app,
 * including action creators and other non-component code.
 *
 * Usage:
 * 1. In your root component (RouteProvider), call navigationService.setNavigate(navigate)
 * 2. In action creators or other code, call navigationService.push(path, options)
 */

class NavigationService {
    constructor() {
        this._navigate = null;
    }

    setNavigate(navigate) {
        this._navigate = navigate;
    }

    push(path, options = {}) {
        if (!this._navigate) {
            console.error('NavigationService: navigate function not set. Call setNavigate() first.');
            return;
        }

        const target = typeof path === 'object' && path.pathname ? path.pathname : path;

        // Skip same-path pushes unless the caller explicitly opts in via allowSamePath.
        // Same-path navigation just re-renders the route component — usually a bug, not intent.
        // Use { allowSamePath: true } when you genuinely need to reset state by re-mounting.
        if (typeof target === 'string' && !options.allowSamePath) {
            const current = (window.location.hash || '').replace(/^#/, '').split('?')[0];
            if (current === target) {
                console.log(`[Nav] skipping same-path push → ${target}`);
                return;
            }
        }

        const method = options.replace ? 'replace' : 'push';
        console.groupCollapsed(`[SSO Navigation] ${method} → ${target}`);
        console.log('path:', path, 'options:', options);
        console.trace('Called from');
        console.groupEnd();

        // Support both old hashHistory API and new navigate API
        if (typeof path === 'object' && path.pathname) {
            // Old API: hashHistory.push({ pathname: '/path', state: { ... } })
            console.log(`[Nav] programmatic push → ${path.pathname}`, path.state || '');
            this._navigate(path.pathname, { state: path.state, replace: options.replace });
        } else if (typeof path === 'string') {
            // New API: navigate('/path', { state: { ... }, replace: true })
            console.log(`[Nav] programmatic push → ${path}`, options.state || '');
            this._navigate(path, options);
        } else {
            console.error('NavigationService: Invalid path argument', path);
        }
    }

    replace(path, options = {}) {
        this.push(path, { ...options, replace: true });
    }

    // Alias for compatibility
    get navigate() {
        return this._navigate;
    }
}

// Create a singleton instance
const navigationService = new NavigationService();

// Create a hashHistory-compatible object for backward compatibility
export const hashHistory = {
    push: (path, options) => navigationService.push(path, options),
    replace: (path, options) => navigationService.replace(path, options)
};

export default navigationService;
